import React, { useContext, useState, useEffect, useCallback } from 'react';
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Sound from 'react-sound';

//import { useSpring, animated } from 'react-spring';
//import Typed from "react-typed";
//const { motion } = require('framer-motion');

//import Particles, { initParticlesEngine } from "@tsparticles/react";
//import { loadSlim } from "@tsparticles/slim"; // if you are going to

import {
  Text, BarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, LabelList, ResponsiveContainer
} from 'recharts';

import quizInstanceAdminStatusContext from '../context/quizInstanceAdminStatusContext';
import QuizAdminQuestionList from './QuizAdminQuestionList';
import QuizAdminQuestion from './QuizAdminQuestion';
import QuizApi from '../QuizApi';

export default function QuizAdminStats() {
  /*
    const [ init, setInit ] = useState(false);
    useEffect(() => {
      initParticlesEngine(async (engine) => {
          await loadSlim(engine);
      }).then(() => {
          setInit(true);
      });
    }, []);
  
  const particlesLoaded = (container) => {
      console.log(container);
  };
  */

  const ctx = useContext(quizInstanceAdminStatusContext);
  const [quizStats, setQuizStats] = useState(null);
  const [inputModeAnswersByQuestionId, setInputModeAnswersByQuestionId] = useState({});
  const [selectedQuestionId, setSelectedQuestionId] = useState("");

  const handleBarClick = useCallback((data, idx) => {
    setSelectedQuestionId(curVal => data.id === curVal ? "" : data.id);
  }, []);

  useEffect(() => {
    const fn = async () => {
      const statsResponse = await QuizApi.quizAdminGetStats(ctx.quizAccessId);
      setQuizStats(statsResponse.response);

      // Fetch participant answers so we can show submitted text answers for
      // input-mode questions (the histogram is empty for those).
      const answerResponse = await QuizApi.quizAdminGetAnswerList(ctx.quizAccessShortId, 0);
      const grouped = {};
      const list = (answerResponse && answerResponse.response && answerResponse.response.answerList) || [];
      for (const a of list) {
        if (!a.inputMode) continue;
        if (!grouped[a.questionId]) grouped[a.questionId] = [];
        grouped[a.questionId].push(a);
      }
      setInputModeAnswersByQuestionId(grouped);
    };

    fn();

  }, [ctx]);

  let questionAccuracyData = [];
  let participantLeaderBoard = [];
  let quizInstanceAccuracyPercent = 0;
  let quizResultText = "";
  let difficultQuestionList = [];
  let renderSingleQuestionQuiz = false;
  const answerCountListsByQuestionId = {};

  const correctAnswerIdSet = new Set(
    (ctx.quizInfo.correctAnswerList || []).map(a => a.id)
  );

  if (quizStats) {
    // ===================================================================
    // Bar-chart-per-question result view. Triggered automatically for
    // single-question quizzes, or explicitly via the singleQuestionMode
    // config flag (lecturer wants to see how the group reasoned, not
    // just % correct).
    // ===================================================================
    const singleQuestionFlag = !!(ctx.quizInfo.config && ctx.quizInfo.config.singleQuestionMode);
    if (ctx.quizInfo.questionList.length === 1 || singleQuestionFlag) {
      renderSingleQuestionQuiz = true;
      for (const question of ctx.quizInfo.questionList) {
        const answerCountList = [];
        for (const answer of question.answerList) {
          const answerCount = quizStats.answerHistogram[answer.id];
          answerCountList.push({
            id: answer.id,
            name: answer.text,
            correct: correctAnswerIdSet.has(answer.id),
            percent: Number(((answerCount || 0) / (ctx.quizInfo.participantAnsweredCount || 1) * 100).toFixed(0))
          });
        }

        answerCountListsByQuestionId[question.id] = answerCountList;
      }
    }
    // ============================================================
    // If there's more than one question, we'll show a bar chart 
    // where each bar shows the percentage of correct answers.
    // ============================================================
    else {
      if (quizStats.questionAccuracyStats) {
        questionAccuracyData = quizStats.questionAccuracyStats
          .sort((a, b) => a.order < b.order ? -1 : 1)
          .map(q => ({
            correctPercent: Number(((q.awardedPoints / q.totalMaxPoints) * 100).toFixed(0)),
            name: "Fråga " + q.order,
            id: q.id
          }));
      }

      if (quizStats.participantLeaderboard) {
        participantLeaderBoard = [...quizStats.participantLeaderboard].sort(
          (a, b) => a.percentage < b.percentage ? 1 : -1
        ).slice(0, 3);
      }

      if (quizStats.quizInstanceAccuracyStats) {
        quizInstanceAccuracyPercent = (
          quizStats.quizInstanceAccuracyStats.awardedPoints
          / quizStats.quizInstanceAccuracyStats.totalMaxPoints
        ) * 100;
        if (quizInstanceAccuracyPercent > 70) {
          quizResultText = "Bra jobbat!";
        } else if (quizInstanceAccuracyPercent >= 50) {
          quizResultText = "Det var ok men kan bli bättre.";
        } else if (quizInstanceAccuracyPercent < 50) {
          quizResultText = "Nja, det kanske ni kan göra bättre.";
        }
      }

      if (quizStats.difficultQuestionList && quizStats.difficultQuestionList.length) {
        difficultQuestionList = quizStats.difficultQuestionList
          .filter(q => q && q.id && q.markdown)
          .sort((a, b) => a.correctness < b.correctness ? -1 : 1)
          .slice(0, 3);
      }
    }

  }

  const primaryColors = ctx.quizStyle.colorList.map(c => c.primaryCss);
  const axisStyle = { fill: '#5C5D50' };

  return (
    <>
      {quizStats === null &&
        <Container>
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        </Container>
      }
      {quizStats !== null &&
        <Container fluid id="quizAdminStatsWrapper" className="h-100">
          <Row noGutters className="h-100">
            <Col id="quizAdminStatsLeft" lg={3} className="h-100">
              <QuizAdminQuestionList
                selectedQuestionId={selectedQuestionId}
                quizInfo={ctx.quizInfo}
                quizStyle={ctx.quizStyle}
              />
            </Col>
            <Col id="quizAdminStatsRight" className="pl-4">

              <div className="ml-4 mt-4">
                {ctx.quizInfo.completed && questionAccuracyData.length > 0 &&
                  <>
                    <div className="mb-4">
                      <h1 className="font-weight-bold">{quizResultText}</h1>
                      <h2>Tillsammans hade ni {quizInstanceAccuracyPercent.toFixed(0)} % rätt!</h2>
                    </div>
                    <div>
                    {difficultQuestionList.length > 0 && 
                      <Row>
                        <Col lg={6}>
                          <h5 className="font-weight-bold">Svåraste frågorna</h5>
                          <ul className="w-75 mb-4" style={{ listStyleType: "none", padding: 0 }}>
                            {difficultQuestionList.map(q =>
                              <li key={q.id}>
                                {q.order}. {q.markdown} (<strong>{(q.correctness * 100).toFixed(0)}%</strong> rätt)
                              </li>
                            )}
                          </ul>
                        </Col>
                        <Col>
                          <h5 className="font-weight-bold">Toppresultat</h5>
                          <ul style={{ padding: 0 }}>
                            {participantLeaderBoard.map(p =>
                              <li key={p.alias}>
                                {p.alias || "Anonym"} ({p.percentage} %)
                              </li>
                            )}
                          </ul>
                        </Col>
                      </Row> 
                    }
                    </div>
                  </>
                }
              </div>

              {renderSingleQuestionQuiz &&
                <>
                  {ctx.quizInfo.questionList.map((question, qIdx) => {

                    const entries = answerCountListsByQuestionId[question.id] || [];
                    const showQuestionHeader = ctx.quizInfo.questionList.length > 1;
                    const isInputMode = question.config && question.config.inputMode;
                    const inputModeAnswers = inputModeAnswersByQuestionId[question.id] || [];

                    return (
                      <div className="mt-4">

                        {showQuestionHeader &&
                          <QuizAdminQuestion
                            key={question.id}
                            question={question}
                            questionNumber={qIdx + 1}
                            quizInfo={ctx.quizInfo}
                            color={ctx.quizStyle.colorList[qIdx % ctx.quizStyle.colorList.length]}
                            showAnswers={false}
                          />
                        }

                        {isInputMode &&
                          <div className="ml-2 mt-2">
                            {inputModeAnswers.length === 0 &&
                              <div className="text-muted">Inga svar än.</div>
                            }
                            {inputModeAnswers.length > 0 &&
                              <ul style={{ listStyleType: "none", padding: 0 }}>
                                {inputModeAnswers.map(a =>
                                  <li key={a.id} className="mb-1">
                                    {a.correct
                                      ? <span style={{color: "#28a745", fontWeight: "bold"}}>✓ </span>
                                      : <span style={{color: "#c0392b"}}>✗ </span>
                                    }
                                    <strong>{a.answerText || ""}</strong>
                                    <span className="ml-2 font-italic" style={{fontSize: "smaller"}}>
                                      ({a.participantAlias || "Anonym"})
                                    </span>
                                  </li>
                                )}
                              </ul>
                            }
                          </div>
                        }

                        {!isInputMode &&
                        <div style={{
                          width: "95%",
                          height: `${105 * entries.length}px`
                        }}>

                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={entries}
                              margin={{ top: 20 }}
                              layout="vertical"
                              barSize={20}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                type="number"
                                domain={[0, 100]}
                                orientation='top'
                                label={
                                  {
                                    value: '% svar',
                                    fill: '#5C5D50',
                                    position: "center",
                                    dy: -20
                                  }
                                }
                              />
                              <YAxis
                                type="category"
                                width={30}
                                tick={false}
                              />
                              <Tooltip
                                cursor={false}
                                labelFormatter={() => ""}
                                formatter={(value, name, props) => [`${value}%`, "Antal svar"]}
                              />
                              <Bar
                                cursor="pointer"
                                dataKey="percent"
                              >
                                {
                                  entries.map((entry, cellIdx) => (
                                    <Cell
                                      key={`cell-${cellIdx}`}
                                      fill={primaryColors[qIdx % ctx.quizStyle.colorList.length]}
                                    />
                                  ))
                                }

                                <LabelList
                                  dataKey="name"
                                  position="top"
                                  content={({ x, y, width, value, index }) => {
                                    const entry = entries[index];
                                    const fillColor = primaryColors[qIdx % ctx.quizStyle.colorList.length];
                                    return (
                                      <text
                                        x={x}
                                        y={y}
                                        dx={10}
                                        dy={-10}
                                        fill={fillColor}
                                        textAnchor="start"
                                        className='font-weight-bold'
                                      >
                                        {entry && entry.correct ? "✓ " : ""}{value}
                                      </text>
                                    );
                                  }}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        }

                      </div>
                    );
                  })}
                </>
              }

              {!renderSingleQuestionQuiz &&
                <>
                  <div className="mt-4">
                    {!questionAccuracyData.length && <h5>Det finns inget att visa här ...</h5>}
                    {questionAccuracyData.length > 0 &&
                      <BarChart
                        width={600}
                        height={300}
                        data={questionAccuracyData}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={axisStyle} />
                        <YAxis
                          type="number"
                          tick={axisStyle}
                          domain={[0, 100]}
                          label={() =>
                            <Text
                              x={0}
                              y={0}
                              dx={-150}
                              dy={20}
                              width={180}
                              textAnchor="start"
                              transform="rotate(-90)"
                              fill="#5C5D50"
                            >% rätt</Text>
                          }
                        />
                        <Tooltip cursor={false} formatter={(value, name, props) => [value + "%", "Antal rätt"]} />
                        <Bar cursor="pointer" dataKey="correctPercent" barSize={50} onClick={handleBarClick}>
                          {
                            questionAccuracyData.map((entry, idx) => (
                              <Cell key={`cell-${idx}`} fill={primaryColors[idx % primaryColors.length]} />
                            ))
                          }
                        </Bar>
                      </BarChart>
                    }
                  </div>
                </>
              }
            </Col>
          </Row>
        </Container>
      }
    </>
  );
};